import { useMemo } from 'react'
import { useWeeklyRosteredHours } from '@/features/employee-schedule/hooks/useWeeklyRosteredHours'
import { Position } from '../types/employment.model'
import { useEmploymentProfiles } from './useEmploymentProfiles'

/**
 * Per-employee figures for the management table and the ranking panel.
 *
 * Read this before trusting a number on that screen, half of it is measured
 * and half of it is scenery:
 *
 * REAL, computed from data the backend holds
 *   role                   the employment profile's position, whenever one is
 *                          set for that employee
 *   hoursThisWeek          shifts rostered in the current ISO week
 *   previousHoursThisWeek  the same for the week before
 *
 * PLACEHOLDER, hashed from the user id
 *   status, serviceRating, ordersServed, avgTicket, revenue, attendancePct,
 *   previousServiceRating, previousRevenue, previousAttendancePct
 *
 * The placeholders have no source to compute from: orders exist in the API but
 * nothing records which waiter served one, and there is no attendance or
 * ratings entity at all. They are hashed rather than random so a row keeps the
 * same value between renders instead of flickering, and they must not be
 * presented anywhere as measurements. Deriving them for real means adding
 * those entities to the backend first.
 */

export type EmployeeRole =
  | 'Waiter'
  | 'Chef'
  | 'Bartender'
  | 'Manager'
  | 'Host'

export type EmployeeStatus = 'Active' | 'On leave' | 'Off'

export interface EmployeeMetrics {
  role: EmployeeRole
  status: EmployeeStatus
  serviceRating: number
  ordersServed: number
  avgTicket: number
  revenue: number
  hoursThisWeek: number
  attendancePct: number
  previousServiceRating: number
  previousRevenue: number
  previousHoursThisWeek: number
  previousAttendancePct: number
}

/** The table shows one label per employee; the profile stores the other. */
const ROLE_BY_POSITION: Record<Position, EmployeeRole> = {
  waiter: 'Waiter',
  chef: 'Chef',
  bartender: 'Bartender',
  host: 'Host',
  manager: 'Manager'
}

const ROLES: EmployeeRole[] = [
  'Waiter',
  'Waiter',
  'Waiter',
  'Chef',
  'Bartender',
  'Host',
  'Manager'
]

const STATUSES: EmployeeStatus[] = [
  'Active',
  'Active',
  'Active',
  'Active',
  'Active',
  'Active',
  'Off',
  'On leave'
]

function hash(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function slice(seed: number, byteOffset: number): number {
  return (seed >>> byteOffset) & 0xff
}

function ranged(byte: number, min: number, max: number, step = 1): number {
  const raw = min + (byte / 255) * (max - min)
  return Math.round(raw / step) * step
}

/** Everything with no data behind it, see the note at the top of the file. */
type PlaceholderMetrics = Omit<
  EmployeeMetrics,
  'hoursThisWeek' | 'previousHoursThisWeek'
>

function placeholderMetrics(userId: string): PlaceholderMetrics {
  const seed = hash(userId)
  const prevSeed = hash(`${userId}::prev`)

  const ordersServed = ranged(slice(seed, 12), 25, 250)
  const avgTicket = ranged(slice(seed, 16), 30, 150)
  const previousOrdersServed = ranged(slice(prevSeed, 8), 25, 250)
  const previousAvgTicket = ranged(slice(prevSeed, 16), 30, 150)

  return {
    // Only a fallback: an employee whose employment profile names a position
    // takes the role from there.
    role: ROLES[slice(seed, 0) % ROLES.length],
    status: STATUSES[slice(seed, 4) % STATUSES.length],
    serviceRating: ranged(slice(seed, 8), 3.0, 5.0, 0.1),
    ordersServed,
    avgTicket,
    revenue: ordersServed * avgTicket,
    attendancePct: ranged(slice(seed, 24), 70, 100),
    previousServiceRating: ranged(slice(prevSeed, 0), 3.0, 5.0, 0.1),
    previousRevenue: previousOrdersServed * previousAvgTicket,
    previousAttendancePct: ranged(slice(prevSeed, 24), 70, 100)
  }
}

export function useEmployeeMetrics(userIds: string[] | undefined): {
  metricsByUserId: Map<string, EmployeeMetrics>
  isLoading: boolean
} {
  const { profilesByUserId, isLoading: profilesLoading } =
    useEmploymentProfiles()
  const {
    currentWeek,
    previousWeek,
    isLoading: hoursLoading
  } = useWeeklyRosteredHours()

  const metricsByUserId = useMemo(() => {
    const map = new Map<string, EmployeeMetrics>()

    ;(userIds ?? []).forEach(userId => {
      const placeholder = placeholderMetrics(userId)
      const position = profilesByUserId[userId]?.position

      map.set(userId, {
        ...placeholder,
        role: position ? ROLE_BY_POSITION[position] : placeholder.role,
        hoursThisWeek: currentWeek.get(userId) ?? 0,
        previousHoursThisWeek: previousWeek.get(userId) ?? 0
      })
    })

    return map
  }, [userIds, profilesByUserId, currentWeek, previousWeek])

  return {
    metricsByUserId,
    // Reported so the table can wait rather than show a hashed role and a zero
    // hour count that both change a beat later.
    isLoading: profilesLoading || hoursLoading
  }
}

export default useEmployeeMetrics
